"use strict";

module.exports = /* @ngInject */ function(
  $log,
  $q,
  $http,
  $timeout,
  sideBySideCommandFactory,
  eikonAttributeRepository,
  sideBySideSessionRepository,
  sideBySideMessageHandler,
  sideBySideLinkTypeFactory,
  sideBySideRequestGateway
) {
  var that = this;
  var maxRetries = 30;
  var waitTimer = 1000;
  var websocketConnection;
  var websocketClosedDeferred = $q.defer();

  const checkIfReady = retriesAvailable => {
    $log.info("Check if SxS is ready, attempts left: " + retriesAvailable);
    return getPingUrl().then(function(authUrl) {
      return $http.get(authUrl).catch(function() {
        if (retriesAvailable > 0) {
          return $timeout(function() {
            return checkIfReady(retriesAvailable - 1);
          }, waitTimer);
        }
        return $q.reject(
          new Error(
            "SxS did not become ready within " +
              (maxRetries * waitTimer) / 1000 +
              " seconds"
          )
        );
      });
    });
  };

  const performHandshake = maybeInstanceId => () => {
    return sideBySideCommandFactory
      .getHandshake(maybeInstanceId)
      .then(handshakeCommand => {
        return getSxsUrl().then(function(sxsUrl) {
          return $http.post(sxsUrl, handshakeCommand).then(function(response) {
            $log.info("Handshake returned", response.data);
            var data = response.data;
            if (data.isSuccess === true) {
              return sideBySideSessionRepository.set(data);
            }
            return $q.reject(new Error("Handshake with SxS failed"));
          });
        });
      });
  };

  const launch = command => () => {
    return getSxsUrl().then(function(sxsUrl) {
      return $http.post(sxsUrl, command).then(function(response) {
        var data = response.data;
        if (data.isSuccess === true) {
          return data;
        } else {
          var message =
            "An unexpected error occurred during launch of the app " +
            command.appId;
          $log.error(
            "Launch of " +
              JSON.stringify(command) +
              " failed." +
              JSON.stringify(data)
          );
          return $q.reject(new Error(message));
        }
      });
    });
  };

  const addSubApp = (windowName, windowType, maybeInstanceId) => () => {
    $log.info(
      `Attempting to add Sub App ${windowName} type: ${windowType} instanceId: ${maybeInstanceId}.`
    );
    return getSxsUrl().then(function(sxsUrl) {
      return $http
        .post(
          sxsUrl,
          sideBySideCommandFactory.getAddSubApp(
            windowName,
            sideBySideLinkTypeFactory.getByRediWindowType(windowType),
            maybeInstanceId
          )
        )
        .then(response => {
          $log.info("Add link command returned");
          var data = response.data;
          if (data.subApps && data.subApps.length > 0) {
            return data.subApps[0].subInstanceId;
          }
        });
    });
  };

  const deleteSubApp = instanceId => () => {
    return getSxsUrl().then(function(sxsUrl) {
      return $http
        .post(sxsUrl, sideBySideCommandFactory.getDelSubApp(instanceId))
        .then(function(response) {
          $log.info("Delete Link command returned");
          var data = response.data;
          if (data.isSuccess === true) {
            $log.info("Successfully removed R+ window instance from SxS");
            return data;
          } else {
            return $q.reject(data);
          }
        });
    });
  };

  const postLink = (sourceInstanceId, targetInstanceId) => () => {
    return getSxsUrl().then(function(sxsUrl) {
      return $http
        .post(
          sxsUrl,
          sideBySideCommandFactory.getLink(sourceInstanceId, targetInstanceId)
        )
        .then(function(response) {
          var data = response.data;
          if (data.isSuccess === true) {
            $log.info("Successfully linked R+ window instance to SxS window");
            return data;
          } else {
            return $q.reject(data);
          }
        });
    });
  };

  const postUnlink = (sourceInstanceId, targetInstanceId) => () => {
    return getSxsUrl().then(function(sxsUrl) {
      return $http
        .post(
          sxsUrl,
          sideBySideCommandFactory.getUnlink(sourceInstanceId, targetInstanceId)
        )
        .then(function(response) {
          var data = response.data;
          if (data.isSuccess === true) {
            $log.info("Successfully unlinked R+ window instance to SxS window");
            return data;
          } else {
            return $q.reject(data);
          }
        });
    });
  };

  const postContextChange = (sourceInstanceId, entity) => () => {
    return getSxsUrl().then(function(sxsUrl) {
      return $http
        .post(
          sxsUrl,
          sideBySideCommandFactory.getContextChanged(sourceInstanceId, entity)
        )
        .then(function(response) {
          var data = response.data;
          if (data.isSuccess === true) {
            $log.info("Successfully changed context.");
            return data;
          } else {
            $log.info("Changing context failed: " + data);
            return $q.reject(data);
          }
        });
    });
  };

  const showEikonMessenger = () => () => {
    return getSxsUrl().then(function(sxsUrl) {
      return $http
        .post(sxsUrl, sideBySideCommandFactory.getShowMessenger())
        .then(function(response) {
          var data = response.data;
          if (data.isSuccess === true) {
            $log.info("Successfully showing messenger");
            return data;
          } else {
            $log.error("Error showing messenger: " + JSON.stringify(data));
            return $q.reject(new Error("Error showing Eikon messenger"));
          }
        });
    });
  };

  const getSaveEikonWorkspace = () => () => {
    return getSxsUrl().then(function(sxsUrl) {
      var command = sideBySideCommandFactory.getSaveWorkspace();
      return $http.post(sxsUrl, command).then(function(response) {
        var data = response.data;
        if (data.isSuccess === true) {
          return data;
        } else {
          $log.info("Getting windows to save failed.");
          return $q.reject();
        }
      });
    });
  };

  const getLoadWorkspace = eikonWorkspace => () => {
    return getSxsUrl().then(function(sxsUrl) {
      var command = sideBySideCommandFactory.getLoadWorkspace(
        JSON.stringify(eikonWorkspace)
      );
      return $http.post(sxsUrl, command).then(function(response) {
        var data = response.data;
        if (data.isSuccess === true) {
          return data;
        } else {
          $log.info("Loading eikon workspace failed.");
          return $q.reject();
        }
      });
    });
  };

  const getCloseWorkspace = () => () => {
    return getSxsUrl().then(function(sxsUrl) {
      var command = sideBySideCommandFactory.getCloseWorkspace();
      return $http.post(sxsUrl, command).then(function(response) {
        var data = response.data;
        if (data.isSuccess === true) {
          return data;
        } else {
          $log.info("Closing eikon workspace failed.");
          return $q.reject();
        }
      });
    });
  };

  that.waitUntilReady = () => checkIfReady(maxRetries);

  that.loginWithToken = function(token) {
    return getAuthUrl().then(function(authUrl) {
      $log.info("Log in with token being performed");
      return $http
        .post(authUrl + "/v1", sideBySideCommandFactory.getLoginToken(token))
        .then(function(response) {
          $log.info("Response received from loginWithToken");
          return response.data;
        });
    });
  };

  that.handshake = function(maybeInstanceId) {
    $log.info("Handshake being performed - " + maybeInstanceId);
    return sideBySideRequestGateway.executeHandshake(
      performHandshake(maybeInstanceId)
    );
  };

  that.launchEikonWindow = function(appId, context) {
    var command = sideBySideCommandFactory.getLaunch(appId, context);
    return sideBySideRequestGateway
      .executeSideBySideRequest(launch(command), "launchEikonWindow")
      .then(launchResult => {
        $log.info(`Eikon window launched appId: ${appId}`);
        return launchResult;
      });
  };

  that.launchEikonWindowByUrl = function(url) {
    var command = sideBySideCommandFactory.getLaunchByUrl(url);
    return sideBySideRequestGateway
      .executeSideBySideRequest(launch(command), "launchEikonWindowByUrl")
      .then(launchResult => {
        $log.info(`Eikon window launched url: ${url}`);
        return launchResult;
      });
  };

  that.rediPlusWindowOpened = function(
    windowName,
    windowType,
    maybeInstanceId
  ) {
    $log.info(
      "R+ window opened for linking: " +
        windowName +
        " type: " +
        windowType +
        " maybeInstanceId: " +
        maybeInstanceId
    );
    return sideBySideRequestGateway
      .executeSideBySideRequest(
        addSubApp(windowName, windowType, maybeInstanceId),
        "addSubApp"
      )
      .then(addSubAppResult => {
        $log.info(
          `Sub app added: instanceId ${maybeInstanceId} is : ${addSubAppResult}`
        );
        return addSubAppResult;
      });
  };

  that.rediPlusWindowClosed = function(instanceId) {
    $log.info("R+ window closed for linking, instanceId: " + instanceId);
    return sideBySideRequestGateway
      .executeSideBySideRequest(deleteSubApp(instanceId), "deleteSubApp")
      .then(deleteSubAppResult => {
        $log.info(`Sub app deleted: instanceId ${instanceId}`);
        return deleteSubAppResult;
      });
  };

  that.establishWebsocketConnection = function() {
    $log.info("Establishing websocket connection with Side By Side");
    return getWsUrl(sideBySideSessionRepository.getToken()).then(
      connectToWebsocketAndListen
    );
  };

  that.reset = function() {
    $log.info("Resetting SxS Session Repository");
    sideBySideSessionRepository.reset();
  };

  that.triggerFakeESO = function() {
    var message = JSON.stringify({
      command: "heartbeat",
      status: "eso"
    });
    sideBySideMessageHandler.onMessage(message);
  };

  that.closeSession = function() {
    if (!websocketConnection) {
      return $q.when();
    }
    websocketConnection.close();
    var websocketClosedDeferredPromise = websocketClosedDeferred.promise;
    return websocketClosedDeferredPromise.then(function() {
      websocketClosedDeferred = $q.defer();
    });
  };

  that.onWindowOpened = function(onWindowOpenedCallback) {
    sideBySideMessageHandler.registerEikonApplicationOpenedMessageHandler(
      onWindowOpenedCallback
    );
  };

  that.onWindowClosed = function(windowInstanceName, onWindowClosedCallback) {
    sideBySideMessageHandler.addOnWindowClosedCallback(
      windowInstanceName,
      onWindowClosedCallback
    );
  };

  that.onExclusiveSignOnEvent = function(onExclusiveSignOnEventCallback) {
    sideBySideMessageHandler.registerExclusiveSignOnMessageHandler(
      onExclusiveSignOnEventCallback
    );
  };

  that.addLink = function(sourceInstanceId, targetInstanceId) {
    $log.info(
      "Adding link from " + sourceInstanceId + " to " + targetInstanceId
    );
    return sideBySideRequestGateway
      .executeSideBySideRequest(
        postLink(sourceInstanceId, targetInstanceId),
        "addLink"
      )
      .then(addLinkResult => {
        $log.info(`Link added from ${sourceInstanceId} to ${targetInstanceId}`);
        return addLinkResult;
      });
  };

  that.removeLink = function(sourceInstanceId, targetInstanceId) {
    $log.info(
      "Removing link from " + sourceInstanceId + " to " + targetInstanceId
    );
    return sideBySideRequestGateway
      .executeSideBySideRequest(
        postUnlink(sourceInstanceId, targetInstanceId),
        "removeLink"
      )
      .then(removeLinkResult => {
        $log.info(
          `Link removed from ${sourceInstanceId} to ${targetInstanceId}`
        );
        return removeLinkResult;
      });
  };

  that.contextChanged = function(sourceInstanceId, entity) {
    $log.info(
      "Changing context from " +
        sourceInstanceId +
        " to this: " +
        JSON.stringify(entity)
    );
    return sideBySideRequestGateway
      .executeSideBySideRequest(
        postContextChange(sourceInstanceId, entity),
        "contextChange"
      )
      .then(contextChangeResult => {
        $log.info(
          `Context changed from ${sourceInstanceId} to ${JSON.stringify(
            entity
          )}`
        );
        return contextChangeResult;
      });
  };

  that.shutdownEikonLight = function() {
    return getAuthUrl().then(function(authUrl) {
      return $http
        .post(authUrl + "/v1", sideBySideCommandFactory.getShutdown())
        .then(
          function(response) {
            const data = response.data;
            if (data.isSuccess === true) {
              $log.info("Successfully shutdown Eikon Light");
              return data;
            } else {
              $log.info("Error shutting down Eikon Light: " + data);
              return $q.reject(data);
            }
          },
          error => {
            $log.error("SxS shutdown request failed", error);
            return $q.reject(`Failed to shutdown Eikon Light via SxS`, error);
          }
        );
    });
  };

  that.showMessenger = function() {
    $log.info("Launching Eikon Messenger");
    return sideBySideRequestGateway
      .executeSideBySideRequest(showEikonMessenger(), "messenger")
      .then(() => $log.info(`Launched Eikon Messenger`));
  };

  function connectToWebsocketAndListen(wsUrl) {
    if (!!websocketConnection) {
      $log.info("Websocket connection already exists.");
      return $q.when();
    }
    $log.info("New websocket connection being established.");
    return $q(function(resolve, reject) {
      websocketConnection = new WebSocket(wsUrl);

      websocketConnection.onopen = function() {
        $log.info("Websocket connection opened");
        resolve();
      };
      websocketConnection.onmessage = function(event) {
        sideBySideMessageHandler.onMessage(event.data);
      };
      websocketConnection.onclose = function(event) {
        $log.info("Closing websocket " + JSON.stringify(event));
        websocketConnection = null;
        websocketClosedDeferred.resolve();
      };
      websocketConnection.onerror = function(event) {
        $log.info("Error " + JSON.stringify(event));
        reject("Error on websocket." + JSON.stringify(event));
      };
    });
  }

  that.getEikonWorkspace = function() {
    return sideBySideRequestGateway.executeSideBySideRequest(
      getSaveEikonWorkspace(),
      "getSaveEikonWorkspace"
    );
  };

  that.loadEikonWorkspace = function(eikonWorkspace) {
    return sideBySideRequestGateway.executeSideBySideRequest(
      getLoadWorkspace(eikonWorkspace),
      "loadEikonWorkspace"
    );
  };

  that.closeEikonWorkspace = function() {
    return sideBySideRequestGateway.executeSideBySideRequest(
      getCloseWorkspace(),
      "closeWorkspace"
    );
  };

  function getPort() {
    return eikonAttributeRepository
      .getEikonLightAttributes()
      .then(function(eikonLightAttributes) {
        return eikonLightAttributes.sideBySidePort;
      });
  }

  function getBaseUrl() {
    return getPort().then(function(port) {
      return "http://127.0.0.1:" + port;
    });
  }

  function getWsUrl(sessionToken) {
    return getPort().then(function(port) {
      return (
        "ws://127.0.0.1:" +
        port +
        "/sxs/v1/notifications?sessionToken=" +
        sessionToken +
        "&linkType=" +
        sideBySideLinkTypeFactory.getNotSupported()
      );
    });
  }

  function getUrl(path) {
    return getBaseUrl().then(function(baseUrl) {
      return baseUrl + path;
    });
  }

  function getSxsUrl() {
    return getUrl("/sxs/v1");
  }

  function getAuthUrl() {
    return getUrl("/auth");
  }

  function getPingUrl() {
    return getUrl("/ping");
  }
};



// WEBPACK FOOTER //
// ./app/infrastructure/sideBySideApi.js